import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { IConfig } from '../../Interfaces/IConfig';

interface IProps {
  configObject: IConfig;
  /** Überschrift im Formular-Card (rechts) */
  title: string;
  /** Untertitel unter der Überschrift */
  subtitle?: string;
  /** Optionale Überschrift im Hero-Bereich (links) */
  heroTitle?: string;
  /** Optionaler Beschreibungstext im Hero-Bereich (links) */
  heroDescription?: string;
  children: React.ReactNode;
}

/**
 * Gemeinsamer Rahmen für die öffentlichen (nicht angemeldeten) Seiten —
 * Passwort-Reset-Bestätigung und Einladung annehmen. Spiegelt den Aufbau der
 * Anmeldeseite (LoginMain) wider: Branding-/Hero-Spalte links, Inhalts-Card
 * rechts, damit sich diese Seiten nahtlos ins neue Mandantenportal einfügen.
 */
export const PublicLayout: React.FC<IProps> = ({
  configObject,
  title,
  subtitle,
  heroTitle = 'Ihr direkter Draht zum Notarbüro.',
  heroDescription = 'Verfolgen Sie den Fortschritt Ihrer Vorgänge, tauschen Sie Dokumente sicher aus und behalten Sie alle Termine und Nachrichten an einem Ort im Blick.',
  children,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: { xs: 4, md: 6 },
      }}
    >
      <Grid
        container
        spacing={{ xs: 1.5, md: 5 }}
        sx={{ maxWidth: 1180, alignItems: 'stretch' }}
      >
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            order: { xs: 2, md: 1 },
            display: { xs: 'none', md: 'block' },
          }}
        >
          <Box
            sx={{
              height: '100%',
              borderRadius: 6,
              p: 5,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha('#ffffff', 0.88)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
            }}
          >
            <Box>
              <Box
                component='img'
                alt={`${configObject.company_name} Logo`}
                src={`data:${configObject.logo_mime};base64,${configObject.logo}`}
                sx={{
                  width: 'auto',
                  maxWidth: 340,
                  maxHeight: 130,
                  objectFit: 'contain',
                  mb: 4,
                }}
              />
              <Typography variant='h3' sx={{ mb: 2 }}>
                {heroTitle}
              </Typography>
              <Typography
                variant='body1'
                sx={{ maxWidth: 520, color: 'text.secondary' }}
              >
                {heroDescription}
              </Typography>
            </Box>

            <Stack spacing={1.5} sx={{ mt: { xs: 2.5, md: 4 } }}>
              <Typography variant='body2' sx={{ fontWeight: 700 }}>
                Was Sie hier erledigen können:
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Den aktuellen Stand Ihrer Vorgänge einsehen
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Dokumente sicher hochladen und herunterladen
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Einladungen beantworten und Stammdaten pflegen
              </Typography>
            </Stack>
          </Box>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
          <Card sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <CardContent sx={{ width: '100%', p: { xs: 3, md: 5 } }}>
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  justifyContent: 'center',
                  mb: 4,
                }}
              >
                <Box
                  component='img'
                  alt={`${configObject.company_name} Logo`}
                  src={`data:${configObject.logo_mime};base64,${configObject.logo}`}
                  sx={{
                    width: 'auto',
                    maxWidth: 320,
                    maxHeight: 130,
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Typography variant='h4' sx={{ mb: 1 }}>
                {title}
              </Typography>
              {subtitle !== undefined && (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 4 }}
                >
                  {subtitle}
                </Typography>
              )}

              {children}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
